import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {distinctUntilChanged, map} from 'rxjs/operators';
import {UtilsService} from '@shared/utils/util';
import {FormSchema} from '@shared/widgets/models/schema';
import {StorageService} from '../shared/services/storage.service';
import {
  initialSchemaStoreState,
  SchemaStoreState,
  SchemaStoreStatus,
} from './schema-store-state';

@Injectable({
  providedIn: 'root'
})
export class FormSchemaStoreService {

  /** tenantId::schemaVersion → formId → schema (session-scoped instance cache; AC-29). */
  private cache: { [tenantKey: string]: { [id: string]: FormSchema } } = {};
  private activeTenantKey: string | null = null;

  private readonly stateSubject =
    new BehaviorSubject<SchemaStoreState<FormSchema>>(initialSchemaStoreState());

  readonly state$: Observable<SchemaStoreState<FormSchema>> = this.stateSubject.asObservable();
  readonly status$: Observable<SchemaStoreStatus> = this.state$.pipe(
    map(s => s.status),
    distinctUntilChanged(),
  );
  readonly schemas$: Observable<FormSchema[]> = this.state$.pipe(
    map(s => s.items),
  );
  readonly error$: Observable<unknown | null> = this.state$.pipe(
    map(s => s.error),
    distinctUntilChanged(),
  );

  constructor(private storage: StorageService) {
  }

  getAll(): FormSchema[] {
    if (this.stateSubject.value.status === 'error') {
      return [];
    }
    const key = this.activeTenantKey;
    if (!key || !this.cache[key]) {
      return [];
    }
    return UtilsService.sortModules(Object.values(this.cache[key]));
  }

  get status(): SchemaStoreStatus {
    return this.stateSubject.value.status;
  }

  markLoading(): void {
    this.stateSubject.next({
      status: 'loading',
      items: this.stateSubject.value.items,
      error: null,
    });
  }

  set(formSchemas: FormSchema, id: string, tenantId?: string, schemaVersion?: string | number | null): void {
    const bucket = this.bucket(tenantId, schemaVersion);
    bucket[id] = formSchemas;
    this.emitFromActive();
  }

  /**
   * Atomically replace the active tenant form bucket (AC-37).
   * Prefer this for bulk session loads; use set() for per-form lazy fetch.
   */
  replaceAll(
    entries: { id: string; schema: FormSchema }[],
    tenantId?: string,
    schemaVersion?: string | number | null,
  ): void {
    const key = this.tenantKey(tenantId, schemaVersion);
    const bucket: { [id: string]: FormSchema } = {};
    (entries || []).forEach(e => {
      if (e?.id != null) {
        bucket[e.id] = e.schema;
      }
    });
    this.cache[key] = bucket;
    this.activeTenantKey = key;
    this.emitLoaded(key);
  }

  get(byName: string, tenantId?: string, schemaVersion?: string | number | null): FormSchema {
    return this.bucket(tenantId, schemaVersion)[byName];
  }

  clear(): void {
    const key = this.tenantKey();
    this.cache[key] = {};
    this.activeTenantKey = key;
    this.stateSubject.next(initialSchemaStoreState());
  }

  /** Drop all tenant buckets (logout / full session reset). */
  resetSession(): void {
    this.cache = {};
    this.activeTenantKey = null;
    this.stateSubject.next(initialSchemaStoreState());
  }

  markError(error: unknown): void {
    this.stateSubject.next({
      status: 'error',
      items: [],
      error,
    });
  }

  private emitLoaded(key: string): void {
    const items = UtilsService.sortModules(Object.values(this.cache[key] || {}));
    this.stateSubject.next({status: 'loaded', items, error: null});
  }

  private emitFromActive(): void {
    const key = this.activeTenantKey || this.tenantKey();
    this.emitLoaded(key);
  }

  private bucket(tenantId?: string, schemaVersion?: string | number | null): { [id: string]: FormSchema } {
    const key = this.tenantKey(tenantId, schemaVersion);
    this.activeTenantKey = key;
    if (!this.cache[key]) {
      this.cache[key] = {};
    }
    return this.cache[key];
  }

  private tenantKey(tenantId?: string, schemaVersion?: string | number | null): string {
    const tid = tenantId ?? this.storage.getTenantId();
    const ver = schemaVersion ?? this.storage.getSession()?.schemaVersion ?? '';
    return `${tid}::${ver}`;
  }

}
